import { useContext, useEffect, useRef, useState } from 'react'
import { Droppable } from '@hello-pangea/dnd'
import getUniqueId from 'helpers/getUniqueId'

import DashedBox from 'js/components/DashedBox/DashedBox'
import { Col } from 'js/components/Grid'
import { cloneWidget } from 'js/page/PageEditor/helpers/cloneWidget'
import getTransparentColor from 'js/page/PageEditor/helpers/getTransparentColor'
import PageEditorContext from 'js/page/PageEditor/PageEditorContext'

import AddWidgetButton from './AddWidgetButton'
import styles from './Column.module.css'
import ColumnActions from './ColumnActions'
import Widget from './Widget'

const Column = ({
    rowIndex,
    columnIndex,
    entity,
    numberOfColumns,
    rowBackgroundColor,
    showRowBackgroundColorSetting,
}) => {
    const { setRows, isDragging, widgetEditing, setWidgetEditing } =
        useContext(PageEditorContext)

    const refWidgetView = useRef<HTMLDivElement>(null)

    const [isFocused, setIsFocused] = useState(false)
    const [showWidgetPicker, setShowWidgetPicker] = useState(false)
    const [focusedWidget, setFocusedWidget] = useState(false)

    useEffect(() => {
        setFocusedWidget(false) // Keeps column actions accessible
    }, [isDragging])

    const widgetIsFocused = !!focusedWidget

    const { widgets, width } = entity

    const handleAddWidget = (index, type) => {
        const newWidget = {
            type,
            settings: [],
            widgetGuid: getUniqueId('widget'),
        }

        setRows((prev) =>
            prev.map((row, rIdx) =>
                rIdx === rowIndex
                    ? {
                          ...row,
                          columns: row.columns.map((col, cIdx) => {
                              if (cIdx === columnIndex) {
                                  const newWidgets = [...col.widgets]
                                  newWidgets.splice(index, 0, newWidget)
                                  return { ...col, widgets: newWidgets }
                              }
                              return col
                          }),
                      }
                    : row,
            ),
        )

        handleEditWidget(index, newWidget, true)
    }

    const handleEditWidget = (index, widget, isNew = false) => {
        setWidgetEditing({
            rowIndex,
            columnIndex,
            index,
            columnWidth,
            widget,
            refWidgetView,
            isNew,
        })
    }

    const handleDeleteWidget = (index) => {
        setRows((prev) =>
            prev.map((row, rIdx) =>
                rIdx === rowIndex
                    ? {
                          ...row,
                          columns: row.columns.map((col, cIdx) =>
                              cIdx === columnIndex
                                  ? {
                                        ...col,
                                        widgets: col.widgets.filter(
                                            (_, wIdx) => wIdx !== index,
                                        ),
                                    }
                                  : col,
                          ),
                      }
                    : row,
            ),
        )
        setFocusedWidget(false) // Make column actions accessible again
    }

    const handleAddWidgetBelow = (index, type) => {
        setIsFocused(false) // Hide actions on this widget
        handleAddWidget(index + 1, type)
    }

    const handleDuplicateWidget = (index, widget) => {
        const clonedWidget = cloneWidget(widget)

        setRows((prev) =>
            prev.map((row, rIdx) =>
                rIdx === rowIndex
                    ? {
                          ...row,
                          columns: row.columns.map((col, cIdx) => {
                              if (cIdx === columnIndex) {
                                  const newWidgets = [...col.widgets]
                                  newWidgets.splice(index + 1, 0, clonedWidget)
                                  return { ...col, widgets: newWidgets }
                              }
                              return col
                          }),
                      }
                    : row,
            ),
        )
        setFocusedWidget(false) // Make column actions accessible again
    }

    const showActions = !isDragging && !widgetEditing && isFocused

    const columnWidth = width[0]

    return (
        <Col
            tabletUp={columnWidth / 12}
            className={styles.wrapper}
            style={
                {
                    '--column-border-color':
                        getTransparentColor(rowBackgroundColor),
                } as React.CSSProperties
            }
            data-show-outline={
                showActions && !widgetIsFocused && widgets?.length > 0
            }
            data-number-of-widgets={widgets.length}
            data-show-add-widget={
                showWidgetPicker || (!widgetEditing && isFocused)
            }
            onMouseEnter={() => setIsFocused(true)}
            onMouseLeave={() => setIsFocused(false)}
        >
            <ColumnActions
                show={showActions}
                hide={widgetIsFocused || showRowBackgroundColorSetting}
                width={width}
                rowIndex={rowIndex}
                columnIndex={columnIndex}
                numberOfColumns={numberOfColumns}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
            />

            <Droppable droppableId={`${rowIndex}-${columnIndex}`} type="WIDGET">
                {(provided) => (
                    <div
                        ref={provided.innerRef}
                        className="PageColumnWidgetWrapper"
                        {...provided.droppableProps}
                    >
                        {widgets?.length > 0 &&
                            widgets.map((widget, index) => {
                                const id = `${rowIndex}-${columnIndex}-${index}`

                                return (
                                    <Widget
                                        ref={refWidgetView}
                                        key={widget.widgetGuid}
                                        id={id}
                                        index={index}
                                        entity={widget}
                                        onEdit={() =>
                                            handleEditWidget(index, widget)
                                        }
                                        onDelete={() =>
                                            handleDeleteWidget(index)
                                        }
                                        onAdd={(type) =>
                                            handleAddWidgetBelow(index, type)
                                        }
                                        onDuplicate={() =>
                                            handleDuplicateWidget(index, widget)
                                        }
                                        focusedWidget={focusedWidget}
                                        setFocusedWidget={setFocusedWidget}
                                        rowBackgroundColor={rowBackgroundColor}
                                        columnWidth={columnWidth}
                                    />
                                )
                            })}

                        {widgets?.length === 0 && (
                            <DashedBox className="PageColumnDashedBox" />
                        )}

                        {provided.placeholder}
                    </div>
                )}
            </Droppable>

            {!isDragging && !widgetEditing && widgets.length === 0 && (
                <AddWidgetButton
                    id={`${rowIndex}-${columnIndex}-add-widget`}
                    className="PageAddWidget"
                    show={showWidgetPicker}
                    setShow={setShowWidgetPicker}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    onAdd={(type) => {
                        handleAddWidget(0, type)
                    }}
                />
            )}
        </Col>
    )
}

export default Column
